import { z } from 'zod';

import { WindowKeySchema } from './enums';
import { MarketDateSchema, TickerSchema } from './primitives';
import { BARS_MAX } from './series.schema';

/**
 * The OHLC wire contract (ADR-0045 · `plans-cc/technical-scorecard-ohlcv-plan.md`
 * §1.11). A SEPARATE representation beside the close-only `PriceSeries`, never a
 * widening of it.
 *
 * Every bare `§` below names that feature plan, NOT the authority plan
 * `plans-cc/meridian-terminal-plan.md` — which has no §1.11, and whose §1.4 is
 * the URL-state-container section.
 *
 * Widening `PriceSeries` would force `series-v2` → `series-v3`, and reads are
 * `private, max-age=300`: a client holding a FRESH old body never sends
 * `If-None-Match`, so `HttpRepository` would fail the new schema on the price
 * chart, the Chart Terminal and the stock screen at once for up to five
 * minutes. A separate route makes that window structurally unreachable.
 *
 * Parallel arrays, not `{o,h,l,c}[]` records (ADR-0037's rationale restated):
 * `simpleMovingAverage`, `exponentialMovingAverage`, `macdSeries`,
 * `relativeStrengthIndex` and `chartStudies` all take `readonly number[]`
 * today and must keep taking it unchanged.
 */

/**
 * What is known about corporate actions over this window.
 *
 * Three-valued on purpose. `unknown` is not a pessimistic `verified_none`:
 * "no split was certified over this interval" and "we never scanned this
 * interval" produce identical bars and opposite derivations, so collapsing
 * them would let an unscanned window silently claim a certified history.
 */
export const SPLIT_KNOWLEDGES = ['verified_none', 'complete', 'unknown'] as const;
export const SplitKnowledgeSchema = z.enum(SPLIT_KNOWLEDGES);
export type SplitKnowledge = z.infer<typeof SplitKnowledgeSchema>;

/**
 * Whether the stored volume basis is the certified one. Independent of
 * `splitKnowledge`: volume rows unform in `core` while the split factors stay
 * fully known, so `{ volumeKnowledge: 'unknown', splitKnowledge: 'complete' }`
 * is a valid body, not a contradiction.
 */
export const VOLUME_KNOWLEDGES = ['canonical', 'unknown'] as const;
export const VolumeKnowledgeSchema = z.enum(VOLUME_KNOWLEDGES);
export type VolumeKnowledge = z.infer<typeof VolumeKnowledgeSchema>;

/**
 * What the served suffix actually covers — ON THE AVAILABLE ARM. `truncated` is
 * true iff the suffix is a proper subset of the operational-window session set
 * from the SPY close calendar — including the case where the window endpoint
 * exists but is close-only. Truncation is REPORTED, never a rejection: the
 * route has no warmup floor, and one complete bar is enough to serve.
 *
 * ON THE UNAVAILABLE ARM this same object means something different, and the
 * difference is deliberate rather than an oversight: there is no served suffix
 * (that is what `unavailable` means, §1.11 layer 1 clause 4), so the fields
 * describe the operational window that was EXAMINED — §6.6's "present when
 * something was looked at, null when there was nothing to cover". `barCount` is
 * then the count of operational sessions inspected, and `truncated` carries no
 * served-suffix meaning there. Only `no_complete_ohlc` can have examined
 * anything; the other two reasons are pinned to `coverage: null` by
 * `BenchmarkOhlcResultSchema` below, so the ambiguity is not expressible
 * wherever nothing was looked at.
 *
 * `barCount` is `.positive()`, so a zero-bar coverage is unrepresentable by
 * construction — an empty examination is `null`, never `barCount: 0`.
 *
 * `truncated` is a bare adjective where coding-style §2 asks booleans to be
 * `is`/`has`/`can`/`should`-prefixed. Left as-is ON PURPOSE: §1.11 fixes the
 * wire name in three places and the plan wins on conflict, so `isTruncated`
 * would be a plan edit under ADR-0005 rule 1 (contract text + test +
 * criterion), not a rename. Do not "fix" it here.
 */
export const OhlcCoverageSchema = z.object({
  fromOn: MarketDateSchema,
  toOn: MarketDateSchema,
  barCount: z.number().int().positive().max(BARS_MAX),
  truncated: z.boolean(),
});
export type OhlcCoverage = z.infer<typeof OhlcCoverageSchema>;

/**
 * Finiteness needs no `.finite()`: Zod 4's `z.number()` already rejects
 * `Infinity` and `NaN`, and the call is a deprecated no-op ESLint fails on.
 * `ohlc-series.schema.test.ts` pins both rejections by behaviour instead.
 *
 * Non-nullable by contract (§1.11 layer 1 clause 1): a partly populated bar is
 * not an OHLC bar. Incomplete benchmark history is expressed as the
 * `BenchmarkOhlcResult` unavailable arm, never as a nullable price leg.
 */
const PriceLegSchema = z.array(z.number()).min(1).max(BARS_MAX);

const OhlcSeriesObjectSchema = z.object({
  ticker: TickerSchema,
  window: WindowKeySchema,
  /**
   * Market dates aligned 1:1 with every leg below, STRICTLY ASCENDING —
   * enforced by `checkSessionsAscending`, not merely asserted here. §1.11 layer
   * 1 clause 2 builds the served run by walking the operational sessions
   * BACKWARDS from the latest complete session, so descending is the
   * algorithm's natural intermediate state and a missing final re-reverse
   * satisfies every other refinement: `checkCoverage` is positional, so
   * `fromOn === sessions[0]` and `toOn === sessions[last]` both hold on a
   * reversed array and the body claims a window starting after it ends.
   */
  sessions: z.array(MarketDateSchema).min(1).max(BARS_MAX),
  /* This restates `series.schema.ts`'s sessions declaration rather than sharing
     one. Deliberate: decision A keeps `OhlcSeries` and `PriceSeries` separately
     versionable, and a shared `SessionsSchema` would recouple the two
     representations at exactly the point the ADR separated them — neither could
     then change its session bound without moving the other's ETag. `BARS_MAX`
     is imported because it is a shared CONSTANT, not a shared schema. */
  /* The RAW vendor quartet — the immutable fact, and the basis the DB CHECK
     ordering is asserted over. `core` derives the single adjusted basis from
     it as `pxAdjFactor = adjClose / close`, per bar and fail-closed. */
  open: PriceLegSchema,
  high: PriceLegSchema,
  low: PriceLegSchema,
  close: PriceLegSchema,
  adjClose: PriceLegSchema,
  /**
   * Share count, or `null` where none was observed (ADR-0040 · R2b). A zero is
   * a real halted session and stays DISTINCT from an absent observation —
   * overloading either as the other is a wrong fact, not a formatting choice.
   * Not integer-constrained: the stored basis is a share count today, but the
   * bound the wire owns is "finite, not negative", not the vendor's data type.
   *
   * `-0` IS NORMALISED TO `0`, and that is a port-parity fix rather than
   * fastidiousness. `.nonnegative()` accepts `-0`, and the two adapters then
   * disagree about it: `JSON.stringify(-0)` emits `0`, so the sign is lost
   * crossing HTTP, while `LocalRepository` runs in-process with no JSON
   * round-trip and keeps it. A `-0` volume reaching a surge denominator is
   * `-Infinity` in local mode and `+Infinity` in live mode — the same input,
   * two answers, from the port that exists to guarantee one. Canonicalising
   * here means neither adapter nor any `core` derivation has to be sign-safe,
   * and `Object.is(-0, 0)` is the only way to observe the difference at all.
   */
  volume: z
    .array(
      z
        .number()
        .nonnegative()
        .transform((shares) => (shares === 0 ? 0 : shares))
        .nullable(),
    )
    .min(1)
    .max(BARS_MAX),
  /**
   * Cumulative split factor at each bar, NULLABLE by design. A required
   * non-null `1` standing in for "we do not know" is on ADR-0045's
   * reject-list: it is indistinguishable from a certified no-split history.
   * The strictly-POSITIVE domain is enforced here; the per-bar usability
   * domain of `pxAdjFactor` (§1.4 clause 3) is `core`'s, not the wire's.
   */
  cumSplit: z.array(z.number().positive().nullable()).min(1).max(BARS_MAX),
  splitKnowledge: SplitKnowledgeSchema,
  volumeKnowledge: VolumeKnowledgeSchema,
  coverage: OhlcCoverageSchema,
});

type OhlcSeriesObject = z.infer<typeof OhlcSeriesObjectSchema>;

/** Every array that must stay 1:1 with `sessions`. `cumSplit` is the one implementers drop. */
const PARALLEL_LEGS = [
  'open',
  'high',
  'low',
  'close',
  'adjClose',
  'volume',
  'cumSplit',
] as const satisfies readonly (keyof OhlcSeriesObject)[];

/** True when every leg aligns. Downstream checks index legs positionally, so they need it. */
function checkAlignment(val: OhlcSeriesObject, ctx: z.RefinementCtx): boolean {
  const expected = val.sessions.length;
  let aligned = true;
  for (const leg of PARALLEL_LEGS) {
    if (val[leg].length !== expected) {
      aligned = false;
      ctx.addIssue({
        code: 'custom',
        message: `${leg} must align 1:1 with sessions`,
        path: [leg],
      });
    }
  }
  return aligned;
}

/**
 * Elementwise ordering over the RAW quartet only. `adjClose` is deliberately
 * unconstrained: it lives on a different scale, and a split makes an adjusted
 * close legitimately fall outside the raw bar's range.
 *
 * A finite non-positive `close` is ACCEPTED (§1.4 clause 3) — one bad print
 * must not `400` a whole series; `core` fail-closes that bar instead.
 *
 * The four legs are read inline with an `undefined` guard rather than
 * materialised into a tuple array. An earlier revision built the quartets and
 * narrowed them with a single `as`, which was sound only because
 * `checkAlignment` had returned true in a different function — a guarantee that
 * held right up until someone reordered the chain. Reading them here needs no
 * assertion, allocates nothing, and short-circuits on the first bad bar. The
 * `undefined` arm cannot fire under the alignment gate above (style §4: state
 * what local control flow guarantees), so it `continue`s rather than throwing —
 * a defensive throw here would be permanently unexecuted coverage drag.
 */
function checkOrdering(val: OhlcSeriesObject, ctx: z.RefinementCtx): void {
  for (let i = 0; i < val.open.length; i += 1) {
    const open = val.open[i];
    const high = val.high[i];
    const low = val.low[i];
    const close = val.close[i];
    /* One condition, not two: the `undefined` arms cannot fire under the
       alignment gate, and as a separate statement their `continue` would be a
       line no test can ever reach. Folded in here it sits on the path every
       well-ordered bar already takes. */
    if (
      open === undefined ||
      high === undefined ||
      low === undefined ||
      close === undefined ||
      (high >= Math.max(open, close) && low <= Math.min(open, close))
    ) {
      continue;
    }
    ctx.addIssue({
      code: 'custom',
      message: `bar ${String(i)} violates low <= min(open, close) <= max(open, close) <= high`,
      path: ['high'],
    });
    return;
  }
}

/**
 * Sessions are STRICTLY ascending. §1.11 layer 1 clause 3 binds `fromOn`/`toOn`
 * to positions 0 and n-1, which only answers "what window is this?" if position
 * order is date order — and clause 2 walks the operational sessions BACKWARDS to
 * build the run, so a producer that omits the final re-reverse emits a
 * descending suffix that satisfies alignment, per-bar ordering AND the
 * positional coverage check. The resulting 200 body claims a window whose start
 * is after its end, and every order-dependent study in ADR-0046 (SMA, RSI,
 * MACD, ATR, Donchian, pivots) then returns plausible wrong numbers rather than
 * an error — the failure shape §0.3 exists to prevent.
 *
 * STRICT also rejects a repeated date, which would otherwise double-count one
 * market session in `barCount` and in every aggregate downstream.
 *
 * Lexicographic `<=` on `YYYY-MM-DD` is exactly chronological, so this needs no
 * date parsing and does not widen `MarketDateSchema`'s shape-only contract.
 */
function checkSessionsAscending(val: OhlcSeriesObject, ctx: z.RefinementCtx): void {
  const { sessions } = val;
  for (let i = 1; i < sessions.length; i += 1) {
    const previous = sessions[i - 1];
    const current = sessions[i];
    if (previous === undefined || current === undefined || current > previous) continue;
    ctx.addIssue({
      code: 'custom',
      message: `sessions must be strictly ascending (index ${String(i)})`,
      path: ['sessions', i],
    });
    return;
  }
}

/** Coverage restates the served suffix; a mismatch means two answers to one question. */
function checkCoverage(val: OhlcSeriesObject, ctx: z.RefinementCtx): void {
  const { sessions, coverage } = val;
  const last = sessions.length - 1;
  if (coverage.fromOn !== sessions[0]) {
    ctx.addIssue({
      code: 'custom',
      message: 'coverage.fromOn must be sessions[0]',
      path: ['coverage', 'fromOn'],
    });
  }
  if (coverage.toOn !== sessions[last]) {
    ctx.addIssue({
      code: 'custom',
      message: 'coverage.toOn must be the last session',
      path: ['coverage', 'toOn'],
    });
  }
  if (coverage.barCount !== sessions.length) {
    ctx.addIssue({
      code: 'custom',
      message: 'coverage.barCount must be the session count',
      path: ['coverage', 'barCount'],
    });
  }
}

/**
 * The three knowledge invariants. Asserted INDEPENDENTLY of query success — a
 * 200 body may legitimately carry `unknown`, so these are not "error" states.
 */
/**
 * ONE table, so the predicate and the message it is paired with cannot drift
 * and cannot be extended independently. A nested ternary beside a message
 * record dispatched on the same closed set twice, and only the record was
 * exhaustiveness-checked: a fourth `SplitKnowledge` member was a type error
 * there while the ternary's trailing arm silently applied the `complete` rule
 * to a value nobody had considered. `satisfies Record<SplitKnowledge, …>` makes
 * both halves fail typecheck together, which is the lockstep discipline closed
 * sets carry here (style §5.4 · naming §3.9).
 */
const SPLIT_RULES = {
  unknown: {
    offends: (factor: number | null) => factor !== null,
    rule: 'every cumSplit must be null',
  },
  verified_none: {
    offends: (factor: number | null) => factor !== 1,
    rule: 'every cumSplit must be exactly 1',
  },
  complete: {
    offends: (factor: number | null) => factor === null,
    rule: 'no cumSplit may be null',
  },
} as const satisfies Record<
  SplitKnowledge,
  { offends: (factor: number | null) => boolean; rule: string }
>;

function checkSplitFactors(val: OhlcSeriesObject, ctx: z.RefinementCtx): void {
  const { splitKnowledge, cumSplit } = val;
  const { offends, rule } = SPLIT_RULES[splitKnowledge];
  const offending = cumSplit.findIndex(offends);
  if (offending < 0) return;
  ctx.addIssue({
    code: 'custom',
    message: `splitKnowledge '${splitKnowledge}': ${rule} (index ${String(offending)})`,
    path: ['cumSplit', offending],
  });
}

export const OhlcSeriesSchema = OhlcSeriesObjectSchema.superRefine((val, ctx) => {
  /* The element-level `.min(1)`/`.max(BARS_MAX)` above do NOT gate this
     callback: in Zod 4 only `invalid_type` aborts subsequent refinements, so a
     `too_small`/`too_big` body still arrives here. Restate the bound as a
     precondition. It buys three things: no O(n) refinement work on a payload
     the bounds already rejected, an issue list free of derived nonsense (a
     `barCount` mismatch reported against a zero-length session list), and —
     most durably — it stops these helpers from receiving an `OhlcSeriesObject`
     whose type says `sessions` is non-empty while the value is `[]`, which the
     first helper to index without a guard would turn into a TypeError thrown
     out of `safeParse` at the `HttpRepository` boundary. */
  if (val.sessions.length < 1 || val.sessions.length > BARS_MAX) return;
  /* Ordering, ascending and coverage index the legs positionally, so a
     misaligned payload stops here rather than reporting a cascade of derived
     nonsense. */
  if (!checkAlignment(val, ctx)) return;
  checkSessionsAscending(val, ctx);
  checkOrdering(val, ctx);
  checkCoverage(val, ctx);
  checkSplitFactors(val, ctx);
});
export type OhlcSeries = z.infer<typeof OhlcSeriesSchema>;
